/**
 * Challenge repository – Firestore CRUD + list. Supports video challenges (status, endDate) and legacy.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { createChallenge, isChallengeActive } from '../../domain/entities/Challenge';

const COLLECTION = 'challenges';

export function challengeRepository({ getFirestore = () => db } = {}) {
  const firestore = getFirestore();
  const coll = () => collection(firestore, COLLECTION);

  return {
    async getById(challengeId) {
      const ref = doc(firestore, COLLECTION, challengeId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return createChallenge({ id: snap.id, ...snap.data() });
    },

    async create(data) {
      const ref = await addDoc(coll(), {
        ...data,
        createdAt: new Date(),
      });
      return ref.id;
    },

    /** List challenges with status 'active'; client filters by endDate > now for true active. */
    async listActive(limitCount = 30) {
      const q = query(
        coll(),
        where('status', '==', 'active'),
        orderBy('endDate', 'desc'),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      const items = [];
      const now = new Date();
      snap.forEach((d) => {
        const c = createChallenge({ id: d.id, ...d.data() });
        if (isChallengeActive(c)) items.push(c);
      });
      return items;
    },

    subscribeActive(limitCount, onUpdate) {
      const q = query(
        coll(),
        where('status', '==', 'active'),
        orderBy('endDate', 'desc'),
        limit(limitCount)
      );
      return onSnapshot(q, (snap) => {
        const now = new Date();
        const items = [];
        snap.forEach((d) => {
          const c = createChallenge({ id: d.id, ...d.data() });
          if (isChallengeActive(c)) items.push(c);
        });
        onUpdate(items);
      });
    },

    /** Legacy: list by disabled flag (for old challenges). */
    async listActiveLegacy(limitCount = 30) {
      const q = query(
        coll(),
        where('disabled', '==', false),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      const items = [];
      snap.forEach((d) => items.push(createChallenge({ id: d.id, ...d.data() })));
      return items;
    },

    subscribeList(limitCount, onUpdate) {
      return this.subscribeActive(limitCount, onUpdate);
    },
  };
}

export const challengeRepo = challengeRepository();
